import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  ArrowLeft, User, EnvelopeSimple, Phone, Buildings, MapPin, Briefcase,
  CalendarPlus, PaperPlaneTilt, CreditCard, RocketLaunch, ChatText, CheckCircle,
  Clock, Note, PencilSimple,
} from "@phosphor-icons/react";

const PRIORITIES = ["low", "medium", "high", "urgent"];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [meeting, setMeeting] = useState({ meeting_at: "", channel: "video" });
  const [payment, setPayment] = useState({ amount: 149, currency: "EUR", method: "Bank Transfer", instructions: "" });
  const [proposal, setProposal] = useState({ notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/crm/leads/${id}`),
        config ? Promise.resolve({ data: config }) : api.get("/crm/config"),
      ]);
      setData(d.data);
      if (!config) setConfig(c.data);
    } catch (err) {
      toast.error(extractApiError(err, "Could not load lead"));
      navigate("/crm");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patchLead = async (patch, successMsg) => {
    try {
      const { data: updated } = await api.patch(`/crm/leads/${id}`, patch);
      setData((d) => ({ ...d, lead: updated }));
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(extractApiError(err, "Update failed"));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/crm/leads/${id}/notes`, { note, kind: "note" });
      setNote("");
      await load();
      toast.success("Note added");
    } catch (err) {
      toast.error(extractApiError(err, "Could not add note"));
    } finally {
      setSavingNote(false);
    }
  };

  const scheduleMeeting = async () => {
    if (!meeting.meeting_at) return toast.error("Please enter a meeting date/time");
    try {
      await api.post(`/crm/leads/${id}/schedule-meeting`, meeting);
      await load();
      toast.success("Meeting scheduled — email sent to the lead");
    } catch (err) {
      toast.error(extractApiError(err, "Could not schedule"));
    }
  };

  const sendProposal = async () => {
    try {
      await api.post(`/crm/leads/${id}/send-proposal`, proposal);
      await load();
      toast.success("Proposal email sent");
    } catch (err) {
      toast.error(extractApiError(err, "Could not send"));
    }
  };

  const requestPayment = async () => {
    if (!payment.amount || payment.amount <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/crm/leads/${id}/payment/request`, payment);
      await load();
      toast.success("Payment request sent");
    } catch (err) {
      toast.error(extractApiError(err, "Could not request"));
    }
  };

  const confirmPayment = async (paymentId) => {
    try {
      await api.post(`/crm/payments/${paymentId}/confirm`, { note: "Received" });
      await load();
      toast.success("Payment confirmed — customer notified");
    } catch (err) {
      toast.error(extractApiError(err, "Could not confirm"));
    }
  };

  const activateWorkspace = async () => {
    if (!window.confirm("Activate workspace for this lead? A welcome email will be sent with login credentials.")) return;
    try {
      const { data: r } = await api.post(`/crm/leads/${id}/activate-workspace`);
      await load();
      toast.success(`Workspace activated (${r.workspace_id})`);
    } catch (err) {
      toast.error(extractApiError(err, "Could not activate"));
    }
  };

  if (loading || !data) return <div className="max-w-6xl mx-auto p-8 text-sm text-muted-foreground">Loading lead…</div>;

  const lead = data.lead;
  const payments = data.payments || [];
  const hasPendingPayment = payments.some((p) => p.status === "requested");
  const canActivate = lead.status !== "workspace_activated" && (payments.some((p) => p.status === "confirmed") || lead.status === "payment_confirmed");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="lead-detail-page">
      <Link to="/crm" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6" data-testid="ld-back">
        <ArrowLeft size={14} /> Back to pipeline
      </Link>

      <PageHeader
        eyebrow={config?.stages?.find((s) => s.key === lead.status)?.label || lead.status}
        title={lead.full_name || "—"}
        subtitle={`${lead.company_name} · ${lead.country} · ${lead.industry}`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={lead.status}
              onChange={(e) => patchLead({ status: e.target.value }, "Stage updated")}
              className="bg-card border border-border rounded-lg px-3 py-2.5 text-sm font-semibold"
              data-testid="ld-stage-select"
            >
              {(config?.stages || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select
              value={lead.priority}
              onChange={(e) => patchLead({ priority: e.target.value }, "Priority updated")}
              className="bg-card border border-border rounded-lg px-3 py-2.5 text-sm"
              data-testid="ld-priority-select"
            >
              {PRIORITIES.map((p) => <option key={p} value={p}>Priority: {p}</option>)}
            </select>
          </div>
        }
      />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: profile + objectives + activities */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact + Company */}
          <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="ld-profile">
            <div className="text-overline text-muted-foreground mb-4">Contact & Company</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Row icon={User} label="Full name" value={lead.full_name} />
              <Row icon={EnvelopeSimple} label="Email" value={<a className="text-primary hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>} />
              <Row icon={Phone} label="Phone" value={lead.phone} />
              <Row icon={Buildings} label="Company" value={lead.company_name} />
              <Row icon={MapPin} label="Country" value={lead.country} />
              <Row icon={Briefcase} label="Industry" value={lead.industry} />
              <Row label="Size" value={lead.company_size} />
              <Row label="Revenue" value={lead.annual_revenue} />
              <Row label="Years in business" value={lead.years_in_business} />
              <Row label="Assigned to" value={lead.assigned_to || <em className="text-muted-foreground">Unassigned</em>} />
            </div>
          </div>

          {/* Objectives + needs */}
          <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="ld-objectives">
            <div className="text-overline text-muted-foreground mb-3">Objectives</div>
            <div className="flex flex-wrap gap-2 mb-5">
              {(lead.objectives || []).map((o) => (
                <span key={o} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-teal-soft text-teal font-semibold">{o}</span>
              ))}
              {(lead.objectives || []).length === 0 && <span className="text-xs text-muted-foreground">No objectives declared.</span>}
            </div>
            <div className="text-overline text-muted-foreground mb-2">Business needs</div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{lead.needs || <em className="text-muted-foreground">—</em>}</p>
          </div>

          {/* Timeline */}
          <div className="bg-card border border-border rounded-2xl p-6 card-elev" data-testid="ld-timeline">
            <div className="flex items-center justify-between mb-4">
              <div className="text-overline text-muted-foreground">Activity timeline</div>
              <div className="text-xs text-muted-foreground">{(lead.timeline || []).length} events</div>
            </div>
            <div className="space-y-3">
              {(lead.timeline || []).slice().reverse().map((t, i) => (
                <div key={i} className="flex gap-3">
                  <div className="mt-1 h-6 w-6 rounded-full bg-teal-soft text-teal grid place-items-center flex-shrink-0">
                    <Clock size={12} weight="bold" />
                  </div>
                  <div className="flex-1 pb-3 border-b border-border/50 last:border-0">
                    <div className="text-sm">{t.note}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {t.actor ? `${t.actor} · ` : ""}{new Date(t.at).toLocaleString()} · {t.kind}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-border">
              <div className="text-overline text-muted-foreground mb-2">Add internal note</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Internal comment, follow-up context, next step…"
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                data-testid="ld-note-input"
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={addNote}
                  disabled={savingNote || !note.trim()}
                  className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="ld-note-save"
                >
                  <Note size={14} weight="bold" /> Save note
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Actions */}
        <div className="space-y-6">
          {/* Schedule meeting */}
          <ActionCard icon={CalendarPlus} title="Schedule discovery call" testId="ld-meeting">
            <input
              value={meeting.meeting_at}
              onChange={(e) => setMeeting({ ...meeting, meeting_at: e.target.value })}
              placeholder="e.g. 2026-07-10 15:00 UTC"
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm mb-2"
              data-testid="ld-meeting-at"
            />
            <select
              value={meeting.channel}
              onChange={(e) => setMeeting({ ...meeting, channel: e.target.value })}
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm mb-3"
              data-testid="ld-meeting-channel"
            >
              <option value="video">Video</option>
              <option value="phone">Phone</option>
              <option value="in-person">In-person</option>
            </select>
            <button onClick={scheduleMeeting} className="w-full bg-primary text-primary-foreground rounded-lg py-2 text-sm font-semibold hover:bg-primary/90" data-testid="ld-meeting-submit">
              Schedule & email
            </button>
          </ActionCard>

          {/* Send proposal */}
          <ActionCard icon={PaperPlaneTilt} title="Send proposal" testId="ld-proposal">
            <textarea
              value={proposal.notes}
              onChange={(e) => setProposal({ notes: e.target.value })}
              rows={3}
              placeholder="Short summary or link…"
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm mb-3"
              data-testid="ld-proposal-notes"
            />
            <button onClick={sendProposal} className="w-full bg-foreground text-background rounded-lg py-2 text-sm font-semibold hover:bg-foreground/90" data-testid="ld-proposal-send">
              Send proposal email
            </button>
          </ActionCard>

          {/* Payment */}
          <ActionCard icon={CreditCard} title="Request payment" testId="ld-payment">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="number"
                value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: Number(e.target.value) })}
                className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm"
                data-testid="ld-pay-amount"
              />
              <select
                value={payment.currency}
                onChange={(e) => setPayment({ ...payment, currency: e.target.value })}
                className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm"
                data-testid="ld-pay-currency"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="XOF">XOF</option>
                <option value="NGN">NGN</option>
              </select>
            </div>
            <select
              value={payment.method}
              onChange={(e) => setPayment({ ...payment, method: e.target.value })}
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm mb-2"
              data-testid="ld-pay-method"
            >
              {(config?.payment_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <textarea
              value={payment.instructions}
              onChange={(e) => setPayment({ ...payment, instructions: e.target.value })}
              rows={2}
              placeholder="Payment instructions (IBAN, mobile-money reference…)"
              className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm mb-3"
              data-testid="ld-pay-instructions"
            />
            <button onClick={requestPayment} className="w-full bg-primary text-primary-foreground rounded-lg py-2 text-sm font-semibold hover:bg-primary/90" data-testid="ld-pay-submit">
              Send payment request
            </button>

            {payments.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                {payments.map((p) => (
                  <div key={p.id} className="text-xs border border-border rounded-lg p-2.5" data-testid={`ld-pay-row-${p.id}`}>
                    <div className="flex justify-between items-center">
                      <div className="font-semibold">{(p.amount_minor / 100).toFixed(2)} {p.currency}</div>
                      <div className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-semibold ${
                        p.status === "confirmed" ? "bg-teal-soft text-teal" : "bg-amber-100 text-amber-700"
                      }`}>{p.status}</div>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{p.method} · {new Date(p.created_at).toLocaleString()}</div>
                    {p.status === "requested" && (
                      <button onClick={() => confirmPayment(p.id)} className="mt-2 w-full bg-teal text-white rounded-md py-1.5 text-[11px] font-semibold hover:bg-teal/90" data-testid={`ld-pay-confirm-${p.id}`}>
                        Mark as received
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ActionCard>

          {/* Activate workspace */}
          <ActionCard icon={RocketLaunch} title="Activate workspace" testId="ld-activate">
            {lead.workspace_id ? (
              <div className="text-xs bg-teal-soft/50 border border-teal/30 rounded-lg p-3">
                <div className="inline-flex items-center gap-1.5 text-teal font-semibold">
                  <CheckCircle size={14} weight="fill" /> Workspace live
                </div>
                <div className="mt-1 text-muted-foreground font-mono text-[10px]">{lead.workspace_id}</div>
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-3 leading-relaxed">
                  Creates the customer account, provisions the workspace, and sends the welcome email.
                </div>
                <button
                  onClick={activateWorkspace}
                  disabled={!canActivate}
                  className="w-full bg-teal text-white rounded-lg py-2 text-sm font-semibold hover:bg-teal/90 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ld-activate-submit"
                >
                  {canActivate ? "Activate workspace" : "Confirm a payment first"}
                </button>
              </>
            )}
          </ActionCard>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{label}</div>
      <div className="inline-flex items-start gap-2 text-sm">
        {Icon && <Icon size={14} className="mt-0.5 text-muted-foreground flex-shrink-0" />}
        <span className="break-words">{value || "—"}</span>
      </div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, testId, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 card-elev" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-md bg-teal-soft text-teal grid place-items-center">
          <Icon size={16} weight="duotone" />
        </div>
        <div className="text-sm font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}
